const PIN_HEADER = 'x-eb-event-dashboard-page-origin';
const COOKIE_NAME = 'eventDashboardPageOrigin';
const COOKIE_PREFIX = `${COOKIE_NAME}=`;
const BOOTSTRAP_PROPERTY = '__ebEventDashboardPageOrigin';
const RELOAD_GUARD_KEY = '__ebEventDashboardPageOriginReload';
const RELOAD_BUDGET_KEY = '__ebEventDashboardPageOriginReloadBudget';
const RELOAD_WINDOW_MS = 60_000;
const MAX_RELOAD_ATTEMPTS = 2;
const NEW_ORIGIN_HEADER = 'x-eb-origin-id';
const NEW_ORIGIN_VALUE = 'NEW';
const EFFECTIVE_ORIGIN_HEADER = 'x-eb-event-dashboard-page-origin-effective';
const RELOAD_REQUIRED_HEADER = 'x-eb-event-dashboard-reload';
const EVENT_DASHBOARD_API_PATH = /^\/(?:event-dashboard-webapp\/api|api\/event-dashboard)(?:\/|$)/;

const rejectedDocuments = new WeakSet<object>();

export const eventDashboardPageOriginReloadNavigator = {
  reload: () => {
    if (typeof window !== 'undefined') {
      window.location.reload();
    }
  },
};

function guardedToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    return window.sessionStorage.getItem(RELOAD_GUARD_KEY);
  } catch {
    return null;
  }
}

function registerReloadAttempt(token: string): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = window.sessionStorage.getItem(RELOAD_BUDGET_KEY);
    const previous = raw ? (JSON.parse(raw) as { attempts?: number; updatedAt?: number }) : null;
    const activeBudget =
      previous !== null &&
      Number.isSafeInteger(previous.attempts) &&
      typeof previous.updatedAt === 'number' &&
      Date.now() >= previous.updatedAt &&
      Date.now() - previous.updatedAt < RELOAD_WINDOW_MS;
    const attempts = activeBudget ? (previous!.attempts as number) : 0;
    if (attempts >= MAX_RELOAD_ATTEMPTS) return false;
    window.sessionStorage.setItem(
      RELOAD_BUDGET_KEY,
      JSON.stringify({ token, attempts: attempts + 1, updatedAt: Date.now() }),
    );
    return true;
  } catch {
    return false;
  }
}

function reloadOnce(token: string): void {
  if (typeof window === 'undefined') return;
  rejectedDocuments.add(window);
  try {
    if (window.sessionStorage.getItem(RELOAD_GUARD_KEY) === token) return;
  } catch {
    return;
  }
  if (!registerReloadAttempt(token)) return;
  try {
    window.sessionStorage.setItem(RELOAD_GUARD_KEY, token);
  } catch {
    return;
  }
  eventDashboardPageOriginReloadNavigator.reload();
}

function clearReloadGuard(): void {
  if (typeof window === 'undefined') return;
  if (rejectedDocuments.has(window)) return;
  try {
    window.sessionStorage.removeItem(RELOAD_GUARD_KEY);
  } catch {}
}

type PageOriginSnapshot = Readonly<{ token: string | null; armed: boolean }>;
type DashboardWindow = Window & { __ebEventDashboardPageOrigin?: PageOriginSnapshot };

export function eventDashboardPageOriginBootstrap(seamArmed: boolean): string {
  return (
    '(function(){if(Object.prototype.hasOwnProperty.call(window,"__ebEventDashboardPageOrigin"))return;var armed=' +
    (seamArmed ? 'true' : 'false') +
    ';var token=null;var stale=null;if(armed){var pairs=[];try{pairs=document.cookie.split(";")}catch(e){}for(var i=0;i<pairs.length;i++){var pair=pairs[i].trim();if(pair.indexOf("' +
    COOKIE_PREFIX +
    '")!==0)continue;var raw=pair.slice(' +
    COOKIE_PREFIX.length +
    ');try{raw=decodeURIComponent(raw)}catch(e){}var parts=raw.split("|");if(raw.length>512||!Number.isSafeInteger(Number(parts[2]))||parts.length!==4||parts[0]!=="new"||parts[1]!==window.location.hostname.toLowerCase()||!/^[0-9]+$/.test(parts[2])||!/^[0-9a-f]{64}$/.test(parts[3]))continue;if(Number(parts[2])>Date.now()/1000){token=raw;break}if(stale===null)stale=raw}if(token===null)token=stale}Object.defineProperty(window,"__ebEventDashboardPageOrigin",{value:Object.freeze({token:token,armed:armed}),writable:false,configurable:false});})();'
  );
}

export class EventDashboardPageOriginExpiredError extends Error {
  constructor() {
    super('Reload this event dashboard document before making another page request.');
    this.name = 'EventDashboardPageOriginExpiredError';
  }
}

function snapshot(): PageOriginSnapshot | null {
  if (typeof window === 'undefined') return null;
  return (window as DashboardWindow)[BOOTSTRAP_PROPERTY] ?? null;
}

function snapshotToken(): string | null {
  return snapshot()?.token ?? null;
}

export function hasEventDashboardPageOriginPin(): boolean {
  return Boolean(snapshot()?.token);
}

export function isEventDashboardPageOriginSeamArmed(): boolean {
  return snapshot()?.armed === true;
}

function missingPinKey(): string {
  const host = typeof window !== 'undefined' ? window.location.hostname.toLowerCase() : '';
  return `missing@${host}`;
}

function pageToken(): string | null {
  const token = snapshotToken();
  if (!token) {
    if (isEventDashboardPageOriginSeamArmed()) {
      reloadOnce(missingPinKey());
      throw new EventDashboardPageOriginExpiredError();
    }
    return null;
  }
  const parts = token.split('|');
  if (parts[1] === window.location.hostname.toLowerCase() && Number(parts[2]) > Date.now() / 1000) {
    const guarded = guardedToken();
    if (guarded !== null && guarded !== token) clearReloadGuard();
    return token;
  }
  reloadOnce(token);
  throw new EventDashboardPageOriginExpiredError();
}

function isEventDashboardApiPath(url: URL): boolean {
  return url.origin === window.location.origin && EVENT_DASHBOARD_API_PATH.test(url.pathname);
}

export function eventDashboardPageOriginHeaders(
  input: RequestInfo | URL,
  initHeaders?: HeadersInit,
): Headers {
  const request = typeof Request !== 'undefined' && input instanceof Request ? input : undefined;
  const headers = new Headers(initHeaders ?? request?.headers);
  if (typeof window === 'undefined') return headers;
  let url: URL;
  try {
    url = new URL(request ? request.url : String(input), window.location.href);
  } catch {
    return headers;
  }
  if (!isEventDashboardApiPath(url)) return headers;
  if (!isEventDashboardPageOriginSeamArmed()) {
    headers.delete(PIN_HEADER);
    return headers;
  }
  const token = pageToken();
  if (!token) {
    headers.delete(PIN_HEADER);
    return headers;
  }
  headers.set(PIN_HEADER, token);
  return headers;
}

function isEventDashboardApiResponse(response: Response): boolean {
  if (!response.url) return false;
  try {
    const url = new URL(response.url, window.location.href);
    return isEventDashboardApiPath(url);
  } catch {
    return false;
  }
}

export function checkEventDashboardPageOriginResponse(response: Response): void {
  const token = snapshotToken();
  if (!token && !isEventDashboardPageOriginSeamArmed()) return;
  if (!isEventDashboardApiResponse(response)) return;
  const effective = response.headers.get(EFFECTIVE_ORIGIN_HEADER);
  const origin = response.headers.get(NEW_ORIGIN_HEADER);
  if (
    (response.status === 409 && response.headers.get(RELOAD_REQUIRED_HEADER) === 'required') ||
    effective === 'old'
  ) {
    reloadOnce(token ?? missingPinKey());
    throw new EventDashboardPageOriginExpiredError();
  }
  if (response.ok && (effective === 'new' || (effective === null && origin === NEW_ORIGIN_VALUE))) {
    clearReloadGuard();
  }
}
